import packageInfo from '../../package.json';
import { SummaryUpdatePreferences } from './summaryUpdatePreferences';

const runningUpdates = new Set<string>();

/** Keep ownership through refresh, including when the editor is unmounted. */
export async function withSummaryUpdateLock<T>(key: string, run: () => Promise<T>): Promise<T> {
    if (runningUpdates.has(key)) throw new Error('Dit verslag wordt al aangepast. Wacht totdat die wijziging klaar is.');
    runningUpdates.add(key);
    try {
        if (navigator.locks) {
            return await navigator.locks.request(`notizy:summary-update:${key}`, { ifAvailable: true }, async lock => {
                if (!lock) throw new Error('Dit verslag wordt al in een ander tabblad aangepast.');
                return run();
            });
        }
        return await run();
    } finally {
        runningUpdates.delete(key);
    }
}

/** First snapshot is immediate; later snapshots coalesce without waiting for silence. */
export class SummaryPreviewScheduler {
    private frame: number | null = null;
    private latest = '';
    private started = false;
    private terminal = false;

    constructor(private render: (summary: string) => void) {}

    preview(summary: string) {
        if (this.terminal) return;
        this.latest = summary;
        if (!this.started) {
            this.started = true;
            this.render(summary);
        } else if (this.frame === null) {
            this.frame = requestAnimationFrame(() => {
                this.frame = null;
                if (!this.terminal) this.render(this.latest);
            });
        }
    }

    finish(summary?: string) {
        this.terminal = true;
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
        if (summary !== undefined) this.render(summary);
    }
}

export class SummaryUpdateMeasurement {
    readonly requestId = crypto.randomUUID();
    private started = performance.now();
    private paintFrames = new Set<number>();
    private finished = false;
    private values: Record<string, unknown>;

    constructor(preferences: SummaryUpdatePreferences) {
        this.values = {
            requestId: this.requestId,
            app: 'web',
            appVersion: process.env.REACT_APP_VERSION || packageInfo.version,
            endpointVersion: preferences.summaryUpdateVersion,
            endpoint: `/summarize-stream-${preferences.summaryUpdateVersion}`,
            requestedModel: preferences.llmModel,
            confirmedModel: null,
            provider: 'claude',
            platform: 'default',
            taskId: null,
            firstReceivedPreviewMs: null,
            firstShownPreviewMs: null,
            firstVisibleChangedTextMs: null,
        };
    }

    metadata(metadata?: Record<string, unknown>) {
        if (!metadata) return;
        if (metadata.task_id != null) this.values.taskId = metadata.task_id;
        const model = metadata.llm_model ?? metadata.actual_model ?? metadata.resolved_model;
        if (typeof model === 'string') this.values.confirmedModel = model;
    }

    receivedPreview() {
        if (this.values.firstReceivedPreviewMs === null) this.values.firstReceivedPreviewMs = performance.now() - this.started;
    }

    // Two frames allow a paint between DOM commit and measurement. A hidden
    // document or off-screen report never counts as visible changed text.
    rendered(isVisible: () => boolean, isPreview: boolean) {
        if (this.finished || this.values.firstVisibleChangedTextMs !== null) return;
        const schedule = (callback: () => void) => {
            const frame = requestAnimationFrame(() => {
                this.paintFrames.delete(frame);
                callback();
            });
            this.paintFrames.add(frame);
        };
        schedule(() => schedule(() => {
            if (!isVisible()) return;
            const elapsed = performance.now() - this.started;
            if (this.values.firstVisibleChangedTextMs === null) this.values.firstVisibleChangedTextMs = elapsed;
            if (isPreview && this.values.firstShownPreviewMs === null) this.values.firstShownPreviewMs = elapsed;
            if (this.finished) this.persist();
        }));
    }

    finish(outcome: 'success' | 'error' | 'interrupted') {
        if (this.finished) return;
        this.finished = true;
        this.values.outcome = outcome;
        this.values.completedMs = performance.now() - this.started;
        this.persist();
    }

    cancelPaints() {
        this.paintFrames.forEach(cancelAnimationFrame);
        this.paintFrames.clear();
    }

    private persist() {
        // Diagnostics only: no report text, prompt, JWT, file or user identity.
        try {
            const key = 'notizy:web:summary-update-measurements';
            const previous = JSON.parse(sessionStorage.getItem(key) || '[]') as Record<string, unknown>[];
            const others = previous.filter(item => item.requestId !== this.requestId);
            sessionStorage.setItem(key, JSON.stringify([...others, this.values].slice(-50)));
            window.dispatchEvent(new CustomEvent('notizy:summary-update-measurement', { detail: { ...this.values } }));
        } catch { /* Diagnostics must not affect the update. */ }
    }
}
